import { useEffect } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import { Formik, Form } from 'formik';
import cx from 'classnames';
import FormikTextField from 'components/FormikFields/FormikTextField';
import AuthFormWrapper from 'components/Wrappers/AuthFormWrapper';
import { ALL_ROUTES } from 'config/routes';
import RoundedButton from 'components/Shared/RoundedButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  login as loginReduxAction,
  logout,
  REQUEST_STATUS,
  setIsLoginStatus,
  setUser,
  enqueueSnackbar,
  closeSnackbar,
  SNACKBAR_VARIANTS,
  removeAuthorizationExpiresTimeout,
  navigateToRoute,
  setAutoLogoutDialogOpen,
} from '../../redux_store';
import { useNavigate } from 'react-router-dom';
import { INFO_DURATION } from '../../contexts/NotificationsProvider';
import { PUBLIC_ROUTES } from '../../config/routes/constants/publicRoutes';
import { SERVICE_ROUTES } from '../../config/routes/constants/serviceRoutes';

const useStyles = makeStyles((theme) => ({
  btn: {
    minWidth: 150,
  },
  loginBtn: {
    margin: theme.spacing(1, 0, 2, 0),
  },
}));

const Login = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { en: { notifications, btn, forms } } = useSelector((state) => state.i18n);
  const { isLoginStatus, isLoginErrors, initPath } = useSelector((state) => state.user);

  const login = (values) => {
    dispatch(loginReduxAction(values));
    dispatch(closeSnackbar());
  };

  const pageLoad = () => {
    const location = window.location;
    const hash = location.hash;
    const isLogoutWithNotification = hash === '#logout' || hash === '#auto_logout';
    if (isLogoutWithNotification || hash === '#logout_without_notification') {
      dispatch(setAutoLogoutDialogOpen(false));
      dispatch(navigateToRoute({
        route: {
          path: ALL_ROUTES.login.path,
          removeQueryString: true,
        },
      }));
      dispatch(removeAuthorizationExpiresTimeout({}));
      navigate(ALL_ROUTES.login.path);
      dispatch(logout());
      dispatch(setUser({}));
      if (isLogoutWithNotification) {
        let n_text = '';
        let n_variant = '';
        switch (hash) {
          case '#auto_logout': {
            n_text = notifications.autoLogout;
            n_variant = SNACKBAR_VARIANTS.info;
            break;
          }
          case '#logout': {
            n_text = notifications.logout401;
            n_variant = SNACKBAR_VARIANTS.warning;
            break;
          }
          default: {
            break;
          }
        }
        dispatch(
          enqueueSnackbar({
            message: n_text,
            options: {
              _key: 'logout',
              variant: n_variant,
              autoHideDuration: hash === '#auto_logout' ? null : INFO_DURATION,
              preventDuplicate: true,
            },
          }),
        );
      }
    }

    if (initPath && ![...Object.values(PUBLIC_ROUTES).map(e => e.path), ...Object.values(SERVICE_ROUTES).map(e => e.path)].includes(initPath)) {
      dispatch(
        enqueueSnackbar({
          message: notifications.pleaseLogIn,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        }),
      );
    }
  };

  useEffect(() => {
    pageLoad();
  }, []);

  useEffect(() => {
    if (isLoginStatus === REQUEST_STATUS.failed || isLoginStatus === REQUEST_STATUS.succeeded) {
      setTimeout(() => {
        dispatch(setIsLoginStatus(REQUEST_STATUS.idle));
      }, 2000);
    }
  }, [isLoginStatus]);

  const handlePasswordReset = () => {
    dispatch(navigateToRoute({
      route: {
        path: ALL_ROUTES.passwordReset.path,
        removeQueryString: true,
      },
    }));
  };

  return (
    <AuthFormWrapper>
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        onSubmit={login}
      >
        {({ isValid, dirty }) => <Form>
          <FormikTextField
            type="email"
            name="email"
            label={forms.email}
            autoComplete="email"
            autoFocus
            backendError={isLoginErrors.email}
            margin="normal"
            required
          />
          <FormikTextField
            type="password"
            name="password"
            label={forms.password}
            autoComplete="current-password"
            backendError={isLoginErrors.password}
            margin="normal"
            required
          />

          <Grid container direction="column" alignItems="center">
            <RoundedButton
              type="submit"
              variant="contained"
              color="primary"
              loaderColor="white"
              className={cx(classes.loginBtn, classes.btn)}
              disabled={!isValid || isLoginStatus === REQUEST_STATUS.loading || !dirty}
              isFailed={isLoginStatus === REQUEST_STATUS.failed}
              isLoading={isLoginStatus === REQUEST_STATUS.loading}
              isSuccess={isLoginStatus === REQUEST_STATUS.succeeded}
            >
              {btn.login}
            </RoundedButton>
            <RoundedButton
              className={classes.btn}
              color="secondary"
              onClick={handlePasswordReset}
            >
              {btn.forgotPassword}
            </RoundedButton>
          </Grid>
        </Form>}
      </Formik>
    </AuthFormWrapper>
  );
};

export default Login;
